/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useMemo, useState } from "react";
import { Document, Page } from "react-pdf";
import { UserDocumentWithId } from "@/shared/types/documentTypes";
import { cn } from "@/shared/utils/core";
import { Download, FileText, Printer, ZoomIn, ZoomOut, Trash2 } from "lucide-react";
import "@/shared/utils/pdf-worker";
import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import { useDocumentDelete } from "@/shared/hooks/useDocumentStorage";
import { useInView } from "react-intersection-observer";
import { useCitation } from "./CitationContext";

// Error boundary for PDF page worker errors
class PageErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: () => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (error.message?.includes("sendWithPromise") || error.message?.includes("worker")) {
      this.props.onError();
    }
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

// Lazy loaded page component with intersection observer
const LazyLoadedPage = React.memo(function LazyLoadedPage({
  pageNumber,
  scale,
  width,
}: {
  pageNumber: number;
  scale: number;
  width: number;
}) {
  const [hasLoaded, setHasLoaded] = React.useState(false);
  const [hasError, setHasError] = React.useState(false);
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "1000px",
    triggerOnce: true,
  });

  React.useEffect(() => {
    if (inView && !hasLoaded) {
      setHasLoaded(true);
    }
  }, [inView, hasLoaded]);

  const handlePageError = React.useCallback(() => {
    setHasError(true);
  }, []);

  return (
    <div ref={ref} className="mb-4" data-page-number={pageNumber}>
      {hasLoaded && !hasError ? (
        <PageErrorBoundary onError={handlePageError}>
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            className="shadow-lg"
            width={width > 0 ? width : undefined}
            onLoadError={handlePageError}
            onRenderError={handlePageError}
          />
        </PageErrorBoundary>
      ) : (
        <div
          className="bg-subtle flex items-center justify-center shadow-lg"
          style={{
            width: width > 0 ? width : 600,
            height: width > 0 ? width * 1.4 : 840, // Approximate A4 ratio
          }}
        >
          <span className="text-stone-400 text-sm">
            {hasError ? `Page ${pageNumber} failed to load` : `Page ${pageNumber}`}
          </span>
        </div>
      )}
    </div>
  );
});

export const DocumentPreview = React.memo(function DocumentPreview({
  document,
  className,
  size = 100,
  iframeClassName,
  onDocumentDeleted,
  onRefreshUrl,
}: {
  document: UserDocumentWithId;
  className?: string;
  size?: number;
  iframeClassName?: string;
  onDocumentDeleted?: () => void;
  onRefreshUrl?: () => Promise<void>;
}) {
  const { highlight, highlightKey, clearCitation } = useCitation();
  const [displayFallback, setDisplayFallback] = useState(false);
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(0.9); // 90% actual size, displayed as 100%
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [, setRetryCount] = useState(0);
  const [hasTriedRefresh, setHasTriedRefresh] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const maxRetries = 2;
  const lastPreviewUrlRef = React.useRef<string | undefined>(document.previewUrl);

  const BASE_SCALE = 0.9; // Base scale for 100% display

  const deleteMutation = useDocumentDelete();

  // Measure container width for PDF scaling
  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth - 32; // Subtract padding
        setContainerWidth(width);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Highlight overlay — injected directly into the target page element so it scrolls with the page
  const prevHighlightEl = React.useRef<HTMLDivElement | null>(null);
  const lastHighlightKey = React.useRef<number>(0);

  React.useEffect(() => {
    // Clean up previous highlight
    if (prevHighlightEl.current) {
      prevHighlightEl.current.remove();
      prevHighlightEl.current = null;
    }

    if (!highlight || !containerRef.current) return;

    let cancelled = false;

    const placeHighlight = (pageEl: Element, canvas: HTMLCanvasElement, shouldScroll: boolean) => {
      if (cancelled) return;

      (pageEl as HTMLElement).style.position = "relative";

      const el = window.document.createElement("div");
      el.style.position = "absolute";
      const pad = 8;
      el.style.left = `${canvas.offsetLeft + highlight.left * canvas.offsetWidth - pad}px`;
      el.style.top = `${canvas.offsetTop + highlight.top * canvas.offsetHeight - pad}px`;
      el.style.width = `${highlight.width * canvas.offsetWidth + pad * 2}px`;
      el.style.height = `${highlight.height * canvas.offsetHeight + pad * 2}px`;
      el.style.backgroundColor = "rgba(244, 250, 234, 0.15)";
      el.style.borderRadius = "4px";
      el.style.pointerEvents = "none";
      el.style.zIndex = "10";
      el.style.boxShadow = "0 0 0 9999px rgba(0, 0, 0, 0.5)";

      pageEl.appendChild(el);
      prevHighlightEl.current = el;

      if (shouldScroll) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    const isNewClick = highlightKey !== lastHighlightKey.current;
    if (isNewClick) {
      lastHighlightKey.current = highlightKey;
    }

    // Small delay to let canvas re-render after zoom changes
    const timer = setTimeout(() => {
      if (cancelled || !containerRef.current) return;

      const pageEl = containerRef.current.querySelector(`[data-page-number="${highlight.page}"]`);
      if (!pageEl) return;

      const canvas = pageEl.querySelector("canvas");
      if (canvas) {
        // Canvas already loaded — place highlight immediately
        placeHighlight(pageEl, canvas, isNewClick);
        return;
      }

      // Canvas not loaded yet (lazy-loaded page). Scroll page into view to trigger loading,
      // then watch for canvas to appear.
      if (isNewClick) {
        pageEl.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      // Use MutationObserver to wait for canvas to render
      const observer = new MutationObserver(() => {
        const c = pageEl.querySelector("canvas");
        if (c) {
          observer.disconnect();
          // Give canvas a moment to finish rendering
          setTimeout(() => {
            if (!cancelled) placeHighlight(pageEl, c, isNewClick);
          }, 200);
        }
      });
      observer.observe(pageEl, { childList: true, subtree: true });

      // Safety timeout — stop watching after 5s
      setTimeout(() => observer.disconnect(), 5000);
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (prevHighlightEl.current) {
        prevHighlightEl.current.remove();
        prevHighlightEl.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, highlightKey, scale, containerWidth]);

  // Escape clears the citation highlight before the sheet handles it
  React.useEffect(() => {
    if (!highlight) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        clearCitation();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [highlight, clearCitation]);

  // Reset state when document changes
  React.useEffect(() => {
    setDisplayFallback(false);
    setNumPages(0);
    setRetryCount(0);
    setHasTriedRefresh(false);
    setIsRefreshing(false);
    clearCitation();
  }, [document.id, clearCitation]);

  // Reset retry state when previewUrl changes (e.g., after refresh)
  React.useEffect(() => {
    if (document.previewUrl && document.previewUrl !== lastPreviewUrlRef.current) {
      lastPreviewUrlRef.current = document.previewUrl;
      // If we were refreshing and got a new URL, reset to try again
      if (isRefreshing) {
        setIsRefreshing(false);
        setRetryCount(0);
        setDisplayFallback(false);
      }
    }
  }, [document.previewUrl, isRefreshing]);

  const handleUnpreviewableSrc = useCallback(() => {
    setDisplayFallback(true);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  const onDocumentLoadError = useCallback(
    async (error: Error) => {
      console.error("[PDF Preview] Failed to load PDF document:", {
        error: error.message,
        stack: error.stack,
        documentUrl: document.previewUrl,
        userAgent: navigator.userAgent,
        workerSrc:
          typeof window !== "undefined" && (window as any).pdfjsLib?.GlobalWorkerOptions?.workerSrc,
      });

      // If we haven't tried refreshing the URL yet, try that first
      // This handles expired presigned URLs
      if (!hasTriedRefresh && onRefreshUrl) {
        console.log("[PDF Preview] Attempting to refresh presigned URL...");
        setHasTriedRefresh(true);
        setIsRefreshing(true);
        try {
          await onRefreshUrl();
          // URL refresh will trigger a re-render with new previewUrl
          // The useEffect watching previewUrl will reset state
          return;
        } catch (refreshError) {
          console.error("[PDF Preview] Failed to refresh URL:", refreshError);
          setIsRefreshing(false);
        }
      }

      // Retry a couple times before showing fallback
      setRetryCount((currentRetryCount) => {
        if (currentRetryCount < maxRetries) {
          console.log(`[PDF Preview] Retrying... (attempt ${currentRetryCount + 1}/${maxRetries})`);
          return currentRetryCount + 1;
        } else {
          // Show fallback after max retries
          setDisplayFallback(true);
          return currentRetryCount;
        }
      });
    },
    [document.previewUrl, hasTriedRefresh, onRefreshUrl],
  );

  const zoomIn = useCallback(() => {
    setScale((scale) => {
      const newScale = scale + 0.09; // 10% increments based on BASE_SCALE
      return Math.min(Math.round(newScale * 100) / 100, 1.8); // Max 200% (1.8)
    });
  }, []);

  const zoomOut = useCallback(() => {
    setScale((scale) => {
      const newScale = scale - 0.09; // 10% decrements based on BASE_SCALE
      return Math.max(Math.round(newScale * 100) / 100, 0.45); // Min 50% (0.45)
    });
  }, []);

  const handlePrint = useCallback(() => {
    // Open PDF in new window for printing
    if (document.previewUrl) {
      const printWindow = window.open(document.previewUrl, "_blank");
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  }, [document.previewUrl]);

  const handleDelete = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handlePreviewClick = useCallback(() => {
    clearCitation();
  }, [clearCitation]);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await deleteMutation.mutateAsync(document.id);
      enqueueSnackbar("Document deleted", {
        variant: "success",
        autoHideDuration: 3000,
      });
      setShowDeleteDialog(false);
      onDocumentDeleted?.();
    } catch {
      enqueueSnackbar("Couldn't delete document. Please try again.", {
        variant: "error",
        autoHideDuration: 5000,
      });
    }
  }, [deleteMutation, document.id, onDocumentDeleted]);

  // Check if document is a PDF (by extension or file name)
  const isPdf =
    document.extension === "application/pdf" ||
    document.name?.toLowerCase().endsWith(".pdf") ||
    document.fileName?.toLowerCase().endsWith(".pdf");

  const component = useMemo(() => {
    // Show loading if previewUrl is not yet available or if refreshing URL
    if (!document.previewUrl || isRefreshing) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">
            {isRefreshing ? "Refreshing document..." : "Loading document..."}
          </div>
        </div>
      );
    }

    if (isPdf && !displayFallback) {
      return (
        <div className="flex flex-col h-full pdf-print-container">
          {/* Custom PDF Controls */}
          <div className="flex items-center justify-between p-2 sm:p-3 bg-white border-b border-primary pdf-controls flex-wrap gap-2 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-wrap">
              <IconButton onClick={zoomOut} className="hidden sm:inline-flex" label="Zoom out">
                <ZoomOut />
              </IconButton>
              <span className="text-sm hidden sm:inline">
                {Math.round((scale / BASE_SCALE) * 100)}%
              </span>
              <IconButton onClick={zoomIn} className="hidden sm:inline-flex" label="Zoom in">
                <ZoomIn />
              </IconButton>
              {numPages > 0 && (
                <span className="text-sm sm:text-sm font-medium ml-2 leading-none text-secondary">
                  {numPages} {numPages === 1 ? "page" : "pages"}
                </span>
              )}
            </div>

            <div className="flex items-center gap-1">
              <IconButton
                label="Download"
                onClick={async () => {
                  if (!document.previewUrl) {
                    enqueueSnackbar(TOAST.GENERIC_ERROR, {
                      variant: "error",
                      autoHideDuration: 3000,
                    });
                    return;
                  }

                  try {
                    // Fetch the file as a blob and force download
                    const response = await fetch(document.previewUrl);
                    const blob = await response.blob();

                    // Create object URL and download
                    const url = window.URL.createObjectURL(blob);
                    const link = window.document.createElement("a");
                    link.href = url;
                    link.download = document.name || "document.pdf";
                    link.style.display = "none";
                    window.document.body.appendChild(link);
                    link.click();

                    // Cleanup
                    window.document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                  } catch {
                    // Fallback to opening in new tab if fetch fails
                    window.open(document.previewUrl, "_blank");
                  }
                }}
              >
                <Download />
              </IconButton>
              <IconButton label="Print" onClick={handlePrint}>
                <Printer />
              </IconButton>
              <IconButton label="Delete" variant="destructive" onClick={handleDelete}>
                <Trash2 />
              </IconButton>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="flex-1 overflow-auto bg-background" onClick={handlePreviewClick}>
            <div ref={containerRef} className="flex flex-col items-center p-2 sm:p-4">
              <Document
                key={`pdf-${document.id}`}
                file={document.previewUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex items-center justify-center h-64">
                    <div className="text-gray-500">Loading PDF...</div>
                  </div>
                }
                error={
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <FileText size={48} className="mb-2" />
                    <p>Failed to load PDF</p>
                    <Button variant="outline" size="lg" className="mt-2 flex items-center">
                      <Download className="mr-2 w-4 h-4" /> Download
                    </Button>
                  </div>
                }
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <LazyLoadedPage
                    key={`page-${index + 1}`}
                    pageNumber={index + 1}
                    scale={scale}
                    width={containerWidth}
                  />
                ))}
              </Document>
            </div>
          </div>
        </div>
      );
    }

    if (document.extension === "image/jpeg" && !displayFallback) {
      return (
        <img
          src={document.previewUrl}
          alt="Preview"
          style={{
            padding: "0.5rem 0",
          }}
          className="text-center text-wrap text-sm"
          onError={handleUnpreviewableSrc}
        />
      );
    }

    return <FileText size={size} />;
  }, [
    document.previewUrl,
    document.extension,
    document.name,
    document.id,
    isPdf,
    handleUnpreviewableSrc,
    displayFallback,
    isRefreshing,
    size,
    iframeClassName,
    numPages,
    scale,
    containerWidth,
    zoomIn,
    zoomOut,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    handlePrint,
    handleDelete,
    handlePreviewClick,
    BASE_SCALE,
  ]);

  return (
    <>
      <div className={cn(["bg-white h-full w-full overflow-hidden", className])}>{component}</div>
      <ConfirmDeleteDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Document"
        description={`Are you sure you want to delete "${document.name}"? This action cannot be undone.`}
      />
    </>
  );
});
